import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { customerAPI } from '@/services/customerAPI';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  FileText,
  Search,
  ArrowRight,
  AlertCircle,
} from 'lucide-react';
import { formatCurrency } from '@/utils/searchValidation';
import { motion } from 'framer-motion';

export const DashboardPage: React.FC = () => {
  const { user } = useCustomerAuth();

  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['account-stats', user?.userId],
    queryFn: () => customerAPI.getAccountStats(user!.userId),
    enabled: !!user?.userId,
  });

  const { data: batches, isLoading: batchesLoading } = useQuery({
    queryKey: ['batch-history', user?.userId],
    queryFn: () => customerAPI.getBatchHistory(user!.userId, 5),
    enabled: !!user?.userId,
  });

  const { data: searches, isLoading: searchesLoading } = useQuery({
    queryKey: ['search-history', user?.userId],
    queryFn: () => customerAPI.getSearchHistory(user!.userId, 5),
    enabled: !!user?.userId,
  });

  if (statsError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="p-6 max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Failed to Load Dashboard</h2>
          <p className="text-sm text-slate-600 mb-4">
            {statsError instanceof Error ? statsError.message : 'An unexpected error occurred'}
          </p>
          <Button onClick={() => window.location.reload()} size="sm">Retry</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">
          Welcome back, {user?.name?.split(' ')[0] || 'there'}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          {stats?.activeSince
            ? `Member since ${new Date(stats.activeSince).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
            : 'CollectABILITY Account Dashboard'
          }
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-sm text-slate-600 mb-1">Batches</div>
          <div className="text-2xl font-bold text-slate-900">
            {statsLoading ? '-' : stats?.totalBatchesProcessed || 0}
          </div>
        </Card>

        <Card className="p-4">
          <div className="text-sm text-slate-600 mb-1">Searches</div>
          <div className="text-2xl font-bold text-slate-900">
            {statsLoading ? '-' : stats?.totalSearchesPerformed || 0}
          </div>
        </Card>

        <Card className="p-4">
          <div className="text-sm text-slate-600 mb-1">Records Processed</div>
          <div className="text-2xl font-bold text-slate-900">
            {statsLoading ? '-' : stats?.totalRowsProcessed.toLocaleString() || 0}
          </div>
        </Card>

        <Card className="p-4">
          <div className="text-sm text-slate-600 mb-1">Total Spent</div>
          <div className="text-2xl font-bold text-slate-900">
            {statsLoading ? '-' : formatCurrency(stats?.totalSpent || 0)}
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Batches */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Recent Batches
            </h2>
            <Link to="/account/batches">
              <Button variant="ghost" size="sm" className="text-xs h-8 gap-1">
                View All
                <ArrowRight className="w-3 h-3" />
              </Button>
            </Link>
          </div>

          {batchesLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse h-14 bg-slate-100 rounded" />
              ))}
            </div>
          ) : batches && batches.length > 0 ? (
            <div className="space-y-2">
              {batches.map((batch) => (
                <div
                  key={batch.id}
                  className="p-3 border border-slate-200 rounded hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {batch.fileName}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {batch.successfulRows} of {batch.rowCount} rows • {formatCurrency(batch.cost)}
                      </div>
                    </div>
                    <div className={`text-xs px-2 py-0.5 rounded ${
                      batch.status === 'completed' ? 'bg-green-50 text-green-700 border border-green-200' :
                      batch.status === 'processing' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                      batch.status === 'failed' ? 'bg-red-50 text-red-700 border border-red-200' :
                      'bg-slate-50 text-slate-700 border border-slate-200'
                    }`}>
                      {batch.status}
                    </div>
                  </div>
                </div>
              ))}
              <Link to="/batch" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <Button variant="outline" size="sm" className="w-full mt-2 h-8 text-xs">
                  Upload New Batch
                </Button>
              </Link>
            </div>
          ) : (
            <div className="text-center py-10">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500 mb-3">No batches yet</p>
              <Link to="/batch" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <Button size="sm" className="h-8 text-xs">Upload Your First Batch</Button>
              </Link>
            </div>
          )}
        </Card>

        {/* Recent Searches */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Search className="w-4 h-4" />
              Recent Searches
            </h2>
            <Link to="/account/searches">
              <Button variant="ghost" size="sm" className="text-xs h-8 gap-1">
                View All
                <ArrowRight className="w-3 h-3" />
              </Button>
            </Link>
          </div>

          {searchesLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse h-14 bg-slate-100 rounded" />
              ))}
            </div>
          ) : searches && searches.length > 0 ? (
            <div className="space-y-2">
              {searches.map((search) => (
                <div
                  key={search.id}
                  className="p-3 border border-slate-200 rounded hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">
                        {search.searchRequest?.firstName || 'Unknown'} {search.searchRequest?.lastName || 'Person'}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {search.searchType?.replace('_', ' + ') || 'Search'} • {formatCurrency(search.cost || 0)}
                      </div>
                    </div>
                    {search.hasResults ? (
                      <Link to={`/account/searches/${search.id}`}>
                        <Button variant="ghost" size="sm" className="h-7 text-xs px-2">View</Button>
                      </Link>
                    ) : (
                      <div className="text-xs px-2 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-200">
                        No Hit
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <Link to="/search" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <Button variant="outline" size="sm" className="w-full mt-2 h-8 text-xs">
                  New Search
                </Button>
              </Link>
            </div>
          ) : (
            <div className="text-center py-10">
              <Search className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500 mb-3">No searches yet</p>
              <Link to="/search" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <Button size="sm" className="h-8 text-xs">Perform Your First Search</Button>
              </Link>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
